"use client";

import { cn, Button } from "@oreme/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import React, {
  type ReactElement,
  type HTMLAttributes,
  useReducer,
} from "react";
import { ThemeIcon } from "@/components";
import { ThemeTypeEnum } from "@/types";

const themeTileVariants = cva(
  "group relative p-2 border rounded-lg bg-primary-foreground transition overflow-hidden",
);

const themeTileContentVariants = cva("rounded-[8px]", {
  variants: {
    theme: {
      earth:
        "text-white bg-orange-500 group-hover:text-orange-50 group-hover:bg-orange-500",
      water:
        "text-white bg-cyan-400 group-hover:text-cyan-50 group-hover:bg-cyan-400",
      universe:
        "text-white bg-violet-500 group-hover:text-violet-50 group-hover:bg-violet-500",
      biodiversity:
        "text-white bg-green-500 group-hover:text-green-50 group-hover:bg-green-500",
    },
  },
});

const themeTileActionVariants = cva(
  "overflow-hidden bg-transparent border border-white",
  {
    variants: {
      theme: {
        earth:
          "group-hover:border-orange-50 group-hover:bg-orange-50 group-hover:text-orange-500",
        water:
          "group-hover:border-cyan-50 group-hover:bg-cyan-50 group-hover:text-cyan-400",
        universe:
          "group-hover:border-violet-50 group-hover:bg-violet-50 group-hover:text-violet-500",
        biodiversity:
          "group-hover:border-green-50 group-hover:bg-green-50 group-hover:text-green-500",
      },
    },
  },
);

type ThemeTileVariantsProps = VariantProps<typeof themeTileVariants>;

interface ThemeTileProps
  extends HTMLAttributes<HTMLDivElement>, ThemeTileVariantsProps {
  theme: ThemeTypeEnum;
}

function ThemeTile({
  theme,
  className,
}: Readonly<ThemeTileProps>): ReactElement {
  const t = {
    theme: useTranslations("global.Theme"),
    section: useTranslations("home.SectionThemeItem"),
  };
  const [isHovered, toggleHovered] = useReducer((s) => {
    return !s;
  }, false);

  const label = t.theme(theme);
  return (
    <div className={cn(themeTileVariants({ className }))}>
      <div className={cn(themeTileContentVariants({ theme }))}>
        <figure className="absolute top-1/2 -translate-y-1/2 -right-2 flex justify-end items-end">
          <ThemeIcon size={180} theme={theme} />
        </figure>
        <div className="relative z-10 p-8">
          <Link
            className="after:absolute after:inset-0"
            href={`/sites?theme=${theme}`}
            onMouseEnter={() => toggleHovered()}
            onMouseLeave={() => toggleHovered()}
          >
            <h3 className="text-lg font-semibold mb-4">{label}</h3>
            <Button
              size="sm"
              variant="outline"
              className={cn(themeTileActionVariants({ theme }))}
            >
              {t.section("action")}
              {isHovered ? (
                <ArrowRight className="animate__animated animate__slideInRight animate__faster" />
              ) : null}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export { ThemeTile, type ThemeTileProps };
